import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useRef, useState, type ChangeEvent } from 'react';

import { Input } from '@terra/ui';

import { api } from '../lib/api';

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

interface AsyncSelectProps<T> {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  label?: string;
}

export function AsyncUserSelect({ value, onChange, placeholder, label }: AsyncSelectProps<any>) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const debouncedQuery = useDebounce(query, 300);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['users-search', debouncedQuery],
    queryFn: () => api.users.listUsers({ q: debouncedQuery, pageSize: 5 }),
    enabled: isOpen,
  });

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <Input
        label={label}
        placeholder={placeholder ?? 'Search users (name, email, phone, ID)...'}
        value={isOpen ? query : value}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          setQuery(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
      />
      {isOpen && (
        <div className="border-border/60 bg-surface absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-xl border shadow-lg">
          {isLoading ? (
            <div className="text-ink-muted p-3 text-[13px]">Loading...</div>
          ) : data?.data.length === 0 ? (
            <div className="text-ink-muted p-3 text-[13px]">No users found.</div>
          ) : (
            <ul className="py-1">
              {data?.data.map((user: any) => (
                <li
                  key={user.id}
                  className="hover:bg-ink/5 cursor-pointer px-4 py-2 text-[13px]"
                  onClick={() => {
                    onChange(user.id);
                    setQuery('');
                    setIsOpen(false);
                  }}
                >
                  <div className="text-ink font-bold">{user.name}</div>
                  <div className="text-ink-subtle text-[11px]">
                    {user.email} &bull; {user.id}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export function AsyncVendorSelect({ value, onChange, placeholder, label }: AsyncSelectProps<any>) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const debouncedQuery = useDebounce(query, 300);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['vendors-search', debouncedQuery],
    queryFn: () => api.vendors.list({ q: debouncedQuery, pageSize: 5 }),
    enabled: isOpen,
  });

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <Input
        label={label}
        placeholder={placeholder ?? 'Search vendors (store name, slug, ID)...'}
        value={isOpen ? query : value}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          setQuery(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
      />
      {isOpen && (
        <div className="border-border/60 bg-surface absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-xl border shadow-lg">
          {isLoading ? (
            <div className="text-ink-muted p-3 text-[13px]">Loading...</div>
          ) : data?.data.length === 0 ? (
            <div className="text-ink-muted p-3 text-[13px]">No vendors found.</div>
          ) : (
            <ul className="py-1">
              {data?.data.map((vendor: any) => (
                <li
                  key={vendor.id}
                  className="hover:bg-ink/5 cursor-pointer px-4 py-2 text-[13px]"
                  onClick={() => {
                    onChange(vendor.id);
                    setQuery('');
                    setIsOpen(false);
                  }}
                >
                  <div className="text-ink font-bold">{vendor.storeName}</div>
                  <div className="text-ink-subtle text-[11px]">
                    @{vendor.slug} &bull; {vendor.id}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
